import {
    orderEventBookableReservationUpdatedZodSchema,
    type OrderBookableReservationUpdatedEvent,
} from "./order-bookable-table-reservation-updated";
import {
    orderCancelledEventZodSchema,
    type OrderCancelledEvent,
} from "./order-cancelled.event";
import {
    orderItemBookableTimeUpdatedEventZodSchema,
    type OrderItemBookableTimeUpdatedEvent,
} from "./order-item-events/order-item-bookable-time-updated.event";
// import type { OrderChangedEvent } from "./order-change.event";
import z from "zod";

import type { OrderContactInfoChangedEvent } from "@@/shared/orders/order-events/events/order-contact-info-changed.event";
import { orderContactInfoChangedEventZodObject } from "@@/shared/orders/order-events/events/order-contact-info-changed.event";
import type { OrderCreatedFromQuoteEvent } from "@@/shared/orders/order-events/events/order-created-from-quote.event";
import { orderCreatedFromQuoteEventZodSchema } from "@@/shared/orders/order-events/events/order-created-from-quote.event";
import type { OrderImportedEvent } from "@@/shared/orders/order-events/events/order-imported.event";
import { orderImportedEventZodSchema } from "@@/shared/orders/order-events/events/order-imported.event";
import type { OrderConfirmationMailSentEvent } from "@@/shared/orders/order-events/events/order-notification-event/order-confirmation-mail-sent-event";
import {
    orderEventPaymentMethodSwitchedZodObject,
    type OrderPaymentMethodSwitchedEvent,
} from "@@/shared/orders/order-events/events/order-payment-events/order-payment-method-switched.event";
import {
    orderEventAnonymizeZodSchema,
    type OrderAnonymizedEvent,
} from "./order-anonymize-event";
import {
    orderChangeRefundRequestedEventZodSchema,
    type OrderChangeRefundRequestedEvent,
} from "./order-change-refund-requested.event";
import {
    orderChangedEventZodSchema,
    type OrderChangedEvent,
} from "./order-changed-event/order-changed.event";
import {
    orderEventConfirmedZodSchema,
    type OrderConfirmedEvent,
} from "./order-confirmed.event";
import {
    orderCreatedEventZodSchema,
    type OrderCreatedEvent,
} from "./order-created.event";
import {
    orderFulfilledEventZodSchema,
    type OrderFulfilledEvent,
} from "./order-fulfilled.event";
import {
    orderFullRefundRequestedEventZodSchema,
    type OrderFullRefundRequestedEvent,
} from "./order-full-refund-requested.event";
import {
    orderItemEventZodSchema,
    type OrderItemEvent,
} from "./order-item-events/_order-item.event";
import {
    orderEventOutForDeliveryZodSchema,
    type OrderOutForDeliveryEvent,
} from "./order-out-for-delivery.event";
import {
    orderPartialRefundRequestedEventZodSchema,
    type OrderPartialRefundRequestedEvent,
} from "./order-partial-refund-requested.event";
import {
    orderPayRequestEventZodSchema,
    type OrderPayRequestEvent,
} from "./order-pay-request-events/_order-pay-request.event";
import {
    orderPaymentEventZodSchema,
    type OrderPaymentEvent,
} from "./order-payment-events/_order-payment.event";
import {
    orderPayLaterOnPremiseCancelledZodSchema,
    type OrderPayLaterOnPremiseCancelledEvent,
} from "./order-payment-events/order-payment-pay-later-on-premise-cancelled.event";
import {
    orderEventPendingPaymentConfirmationZodSchema,
    type OrderPendingPaymentConfirmationEvent,
} from "./order-payment-events/order-pending-payment-confirmation.event";
import {
    orderPendingConfirmationEventZodSchema,
    type OrderPendingConfirmationEvent,
} from "./order-pending-confirmation.event";
import {
    orderEventPendingDeliveryZodSchema,
    type OrderPendingDeliveryEvent,
} from "./order-pending-delivery.event";
import {
    orderEventReadyForPickUpZodSchema,
    type OrderReadyForPickUpEvent,
} from "./order-ready-for-pickup.event";
import {
    orderEventRejectedZodSchema,
    type OrderRejectedEvent,
} from "./order-rejected.event";

type OrderEvent =
    | OrderCreatedFromQuoteEvent
    | OrderCreatedEvent
    | OrderConfirmedEvent
    | OrderFulfilledEvent
    | OrderCancelledEvent
    | OrderBookableReservationUpdatedEvent
    | OrderItemBookableTimeUpdatedEvent
    | OrderOutForDeliveryEvent
    | OrderRejectedEvent
    | OrderReadyForPickUpEvent
    | OrderPendingPaymentConfirmationEvent
    | OrderPendingDeliveryEvent
    | OrderPayLaterOnPremiseCancelledEvent
    | OrderPendingConfirmationEvent
    | OrderChangedEvent
    | OrderPartialRefundRequestedEvent
    | OrderFullRefundRequestedEvent
    | OrderChangeRefundRequestedEvent
    | OrderAnonymizedEvent
    | OrderPaymentMethodSwitchedEvent
    | OrderContactInfoChangedEvent
    | OrderConfirmationMailSentEvent
    // unions of order events:
    | OrderItemEvent
    | OrderPaymentEvent
    | OrderPayRequestEvent
    | OrderImportedEvent;

type OrderEventType = OrderEvent["type"];

const orderEventZodSchema: z.ZodSchema<OrderEvent> = z.union([
    orderCreatedFromQuoteEventZodSchema,
    orderCreatedEventZodSchema,
    orderEventConfirmedZodSchema,
    orderFulfilledEventZodSchema,
    orderCancelledEventZodSchema,
    orderEventBookableReservationUpdatedZodSchema,
    orderItemBookableTimeUpdatedEventZodSchema,
    orderEventOutForDeliveryZodSchema,
    orderEventRejectedZodSchema,
    orderEventReadyForPickUpZodSchema,
    orderEventPendingPaymentConfirmationZodSchema,
    orderEventPendingDeliveryZodSchema,
    orderPayLaterOnPremiseCancelledZodSchema,
    orderPendingConfirmationEventZodSchema,
    orderChangedEventZodSchema,
    orderPartialRefundRequestedEventZodSchema,
    orderFullRefundRequestedEventZodSchema,
    orderChangeRefundRequestedEventZodSchema,
    orderEventAnonymizeZodSchema,
    orderEventPaymentMethodSwitchedZodObject,
    orderContactInfoChangedEventZodObject,
    // unions of order events:
    orderItemEventZodSchema,
    orderPaymentEventZodSchema,
    orderPayRequestEventZodSchema,
    orderImportedEventZodSchema,
]);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isOrderEvent = (event: any): event is OrderEvent => {
    return event?._type === "ORDER_EVENT";
};
export { isOrderEvent, orderEventZodSchema };
export type { OrderEvent, OrderEventType };
